/**
 * IP access restriction service (Sprint 18)
 * Login policy + trusted device management
 */
import api from './api'
import type {
  AdminLoginPolicyUpdate,
  LoginPolicy,
  LoginPolicyUpdate,
  RecentLoginIp,
  TrustedDevice,
} from '../types'

// ── Self-service ──────────────────────────────────────────────────────────────

export async function getMyLoginPolicy(): Promise<LoginPolicy> {
  const resp = await api.get<LoginPolicy>('/v1/auth/login-policy')
  return resp.data
}

export async function updateMyLoginPolicy(data: LoginPolicyUpdate): Promise<LoginPolicy> {
  const resp = await api.put<LoginPolicy>('/v1/auth/login-policy', data)
  return resp.data
}

export async function listMyTrustedDevices(): Promise<TrustedDevice[]> {
  const resp = await api.get<TrustedDevice[]>('/v1/auth/trusted-devices')
  return resp.data
}

export async function revokeMyTrustedDevice(id: number): Promise<void> {
  await api.delete(`/v1/auth/trusted-devices/${id}`)
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export async function adminGetLoginPolicy(userId: number): Promise<LoginPolicy> {
  const resp = await api.get<LoginPolicy>(`/v1/users/${userId}/login-policy`)
  return resp.data
}

/**
 * Admin: set a user's login policy. Unlike the self-service call, this can also
 * set the per-user MFA relaxations (mfa_exempt_cidrs / mfa_required) — every
 * change is audit-logged server-side.
 */
export async function adminSetLoginPolicy(userId: number, data: AdminLoginPolicyUpdate): Promise<LoginPolicy> {
  const resp = await api.put<LoginPolicy>(`/v1/users/${userId}/login-policy`, data)
  return resp.data
}

/**
 * Admin: the addresses this user has actually signed in from recently, each
 * flagged with whether the saved policy already covers it. Lets the admin build
 * an IP policy from real data instead of guessing at one office address.
 */
export async function adminListLoginIps(userId: number): Promise<RecentLoginIp[]> {
  const resp = await api.get<RecentLoginIp[]>(`/v1/users/${userId}/login-ips`)
  return resp.data
}

export async function adminListTrustedDevices(userId: number): Promise<TrustedDevice[]> {
  const resp = await api.get<TrustedDevice[]>(`/v1/users/${userId}/trusted-devices`)
  return resp.data
}

export async function adminRevokeTrustedDevice(userId: number, deviceId: number): Promise<void> {
  await api.delete(`/v1/users/${userId}/trusted-devices/${deviceId}`)
}
